import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import Chart from "@/components/Chart";
import EmptyState from "@/components/EmptyState";
import SectionHeading from "@/components/SectionHeading";
import Select from "@/components/Select";
import SelectableNavList from "@/components/SelectableNavList";
import { sysColors } from "@/assets/theme/colors";
import type { CareerPlanCompetencyScore } from "./careerPlanHistory";
import { getCareerPlanOnboardingComparisonRoleFit } from "./careerPlanOnboarding";
import type { CareerPlanRoleRubric } from "../queries";
import ExperienceIndexArchetypeSection from "./_ExperienceIndexArchetypeSection";
import ExperienceIndexCompetencyStandingRow from "./_ExperienceIndexCompetencyStandingRow";
import ExperienceIndexPriorityGapsSection from "./_ExperienceIndexPriorityGapsSection";
import ExperienceIndexTopCompatibleRolesSection from "./_ExperienceIndexTopCompatibleRolesSection";
import { matchExperienceArchetype } from "./experienceIndexArchetypes";
import { EXPERIENCE_INDEX_NAV_CATEGORIES, type ExperienceIndexCategoryId } from "./experienceIndexCategories";
import { EXPERIENCE_INDEX_CATEGORY_ICONS } from "./experienceIndexIcons";
import {
  canShowExperienceIndexRadar,
  getCareerRoleFits,
  getCompetencyStandingRows,
  getRadarComparisonData
} from "./experienceIndexResults";
import Icon from "@/components/Icon";

const categoryNavOptions = EXPERIENCE_INDEX_NAV_CATEGORIES.map((category) => ({
  value: category.id,
  label: category.label,
  iconName: EXPERIENCE_INDEX_CATEGORY_ICONS[category.id]
}));

const SCORES_SERIES = [
  {
    dataKey: "member",
    name: "Scores",
    color: sysColors["sys-brand-primary"]
  }
];

const ROLE_RUBRIC_SERIES = [
  ...SCORES_SERIES,
  {
    dataKey: "desired",
    name: "Role rubric",
    color: sysColors["sys-brand-outline"],
    fillOpacity: 0.12
  }
];

const SCORES_HEADING = {
  heading: "Experience Index scores",
  body: "Review completed competency scores by category. Select a comparison role to overlay the role rubric."
};

const ROLE_RUBRIC_HEADING = {
  heading: "Role Rubric Comparison",
  body: "Compare completed competency scores to the selected role's rubric. Pick a specific category to see a radar when enough scores match that rubric; All is list-only."
};

const SCORES_HEADING_WITHOUT_ROLES = "Review completed competency scores by category.";

export default function ExperienceIndexResultsTab({
  assessmentComplete = false,
  assessmentComparisonRole = null,
  scores,
  rubrics,
  onSelectCompetency
}: {
  assessmentComplete?: boolean;
  assessmentComparisonRole?: string | null;
  scores: CareerPlanCompetencyScore[];
  rubrics: CareerPlanRoleRubric[];
  onSelectCompetency?: (competencyId: string) => void;
}) {
  const roleFits = useMemo(() => getCareerRoleFits(rubrics, scores), [rubrics, scores]);
  const storedComparisonRoleId = useMemo(
    () => getCareerPlanOnboardingComparisonRoleFit({ assessmentComparisonRole, scores }, rubrics)?.id ?? "",
    [assessmentComparisonRole, rubrics, scores]
  );
  const [comparisonRoleId, setComparisonRoleId] = useState(storedComparisonRoleId);
  const [categoryId, setCategoryId] = useState<ExperienceIndexCategoryId>("all");
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const comparisonRoleFieldRef = useRef<HTMLDivElement>(null);
  const focusRemoveOnSelectRef = useRef(false);
  const focusSelectOnRemoveRef = useRef(false);
  const comparisonRoleTouchedRef = useRef(false);

  useEffect(() => {
    setComparisonRoleId((current) => {
      const currentStillValid = roleFits.some((fit) => fit.id === current);
      if (comparisonRoleTouchedRef.current) {
        return currentStillValid ? current : "";
      }
      return storedComparisonRoleId && roleFits.some((fit) => fit.id === storedComparisonRoleId)
        ? storedComparisonRoleId
        : "";
    });
  }, [roleFits, storedComparisonRoleId]);

  const selectedRoleFit = roleFits.find((fit) => fit.id === comparisonRoleId) ?? null;
  const standingRows = useMemo(
    () => getCompetencyStandingRows(selectedRoleFit, scores, categoryId),
    [categoryId, scores, selectedRoleFit]
  );
  const radarData = useMemo(
    () => getRadarComparisonData(selectedRoleFit, scores, categoryId),
    [categoryId, scores, selectedRoleFit]
  );
  const showRadar = canShowExperienceIndexRadar(radarData, categoryId);
  const roleOptions = useMemo(
    () =>
      roleFits.map((fit) => ({
        value: fit.id,
        displayText: fit.title
      })),
    [roleFits]
  );
  const heading = selectedRoleFit
    ? ROLE_RUBRIC_HEADING
    : {
        heading: SCORES_HEADING.heading,
        body: roleOptions.length > 0 ? SCORES_HEADING.body : SCORES_HEADING_WITHOUT_ROLES
      };
  const radarSeries = selectedRoleFit ? ROLE_RUBRIC_SERIES : SCORES_SERIES;
  const selectedCategoryLabel =
    EXPERIENCE_INDEX_NAV_CATEGORIES.find((category) => category.id === categoryId)?.label ?? categoryId;
  const archetype = useMemo(
    () => (assessmentComplete ? matchExperienceArchetype(scores) : null),
    [assessmentComplete, scores]
  );

  useLayoutEffect(() => {
    if (focusRemoveOnSelectRef.current && comparisonRoleId) {
      removeButtonRef.current?.focus();
      focusRemoveOnSelectRef.current = false;
      return;
    }

    if (focusSelectOnRemoveRef.current && !comparisonRoleId) {
      comparisonRoleFieldRef.current
        ?.querySelector<HTMLElement>('[data-testid="career-plan-comparison-role-select-search"]')
        ?.focus();
      focusSelectOnRemoveRef.current = false;
    }
  }, [comparisonRoleId]);

  const handleSelectComparisonRole = (roleId: string) => {
    comparisonRoleTouchedRef.current = true;
    focusRemoveOnSelectRef.current = true;
    setComparisonRoleId(roleId);
  };

  if (scores.length === 0) {
    return (
      <EmptyState
        title="No Experience Index Results"
        iconName="quiz"
        caption="No Experience Index results are available for this snapshot."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="career-plan-ei-results-tab">
      {archetype ? (
        <ExperienceIndexArchetypeSection
          archetype={archetype}
          scores={scores}
          onSelectCompetency={onSelectCompetency}
        />
      ) : null}

      {roleOptions.length > 0 ? (
        <div className="mt-12 flex flex-col gap-2">
          {!comparisonRoleId ? (
            <div ref={comparisonRoleFieldRef}>
              <Select
                label="Comparison role"
                options={roleOptions}
                value={comparisonRoleId}
                onSelect={handleSelectComparisonRole}
                placeholder="Select a comparison role"
                searchable
                optionListMaxHeight="extended"
                data-testid="career-plan-comparison-role-select"
              />
            </div>
          ) : selectedRoleFit ? (
            <div
              className="flex min-h-[3.5rem] w-full flex-row items-center rounded border border-sys-brand-outline px-4 py-2"
              data-testid="career-plan-comparison-role-selected"
            >
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="text-label-large text-sys-brand-on-surface-variant">Comparison role</p>
                <p
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-body-large-semibold text-sys-brand-on-surface"
                  title={selectedRoleFit.title}
                >
                  {selectedRoleFit.title}
                </p>
              </div>
              <button
                ref={removeButtonRef}
                type="button"
                className="ml-4 flex shrink-0 items-center justify-center rounded-full p-1 transition-colors hover:bg-sys-brand-surface-container-high"
                onClick={() => {
                  comparisonRoleTouchedRef.current = true;
                  focusSelectOnRemoveRef.current = true;
                  setComparisonRoleId("");
                }}
                aria-label="Remove comparison role"
                data-testid="career-plan-comparison-role-remove"
              >
                <Icon name="close" size={20} className="text-sys-brand-on-surface-variant" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {selectedRoleFit ? (
        <ExperienceIndexPriorityGapsSection
          roleFit={selectedRoleFit}
          scores={scores}
          onSelectCompetency={onSelectCompetency}
        />
      ) : null}

      <SectionHeading heading={heading.heading} body={heading.body} />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <SelectableNavList
          className="w-full shrink-0 lg:w-80"
          label="Category to view"
          options={categoryNavOptions}
          value={categoryId}
          onChange={(value) => setCategoryId(value as ExperienceIndexCategoryId)}
          data-testid="career-plan-category-nav"
        />

        <div className="min-w-0 flex-1" data-testid="career-plan-role-rubric-detail">
          {standingRows.length === 0 ? (
            <EmptyState
              title="No Competencies in This Category"
              iconName="quiz"
              caption={`There are no Experience Index competencies for ${selectedCategoryLabel}.`}
            />
          ) : (
            <div className="flex w-full flex-col gap-6 rounded-lg border border-sys-brand-outline p-6">
              {showRadar ? (
                <Chart
                  variant="radar"
                  bordered={false}
                  className="w-full"
                  data={radarData}
                  height={320}
                  maxValue={5}
                  series={radarSeries}
                />
              ) : null}

              <div className="flex flex-col gap-3" data-testid="career-plan-competency-standing">
                {standingRows.map((row) => (
                  <ExperienceIndexCompetencyStandingRow key={row.id} row={row} onViewAnswers={onSelectCompetency} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {assessmentComplete ? (
        <ExperienceIndexTopCompatibleRolesSection comparisonRoleId={comparisonRoleId} roleFits={roleFits} />
      ) : null}
    </div>
  );
}
